import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { EmailRoutingPriority } from '@central/contracts';
import { Button } from '@/components/Button';
import { Card, CardBody } from '@/components/Card';
import { mensagemDeErro } from '@/lib/format';
import {
  apenasAtivos,
  listaDeImpactos,
  subcategoriasDaCategoria,
  useOpcoesDeFormulario,
} from '@/features/tickets/opcoes';
import {
  chavesDeAdministracao,
  obterRoteamentoDeEmail,
  salvarRoteamentoDeEmail,
} from '@/features/admin/admin.api';

const CAMPO =
  'w-full rounded-lg border border-input/80 bg-muted/40 px-3 py-2 text-sm ' +
  'transition-colors hover:bg-muted focus-visible:border-ring/40 focus-visible:bg-background ' +
  'focus-visible:outline-none focus-visible:shadow-glow ' +
  'disabled:cursor-not-allowed disabled:opacity-50';

const ROTULO_PRIORIDADE: Record<EmailRoutingPriority, string> = {
  LOW: 'Baixa',
  MEDIUM: 'Média',
  HIGH: 'Alta',
  CRITICAL: 'Crítica',
};

export function EmailRoutingPage() {
  const queryClient = useQueryClient();
  const consulta = useQuery({
    queryKey: chavesDeAdministracao.roteamentoDeEmail,
    queryFn: obterRoteamentoDeEmail,
  });
  const opcoes = useOpcoesDeFormulario();

  const config = consulta.data;

  const [teamId, setTeamId] = useState<string>('');
  const [priority, setPriority] = useState<string>('');
  const [categoryId, setCategoryId] = useState<string>('');
  const [subcategoryId, setSubcategoryId] = useState<string>('');
  const [inicializado, setInicializado] = useState(false);

  if (config && !inicializado) {
    setTeamId(config.teamId ?? '');
    setPriority(config.priority ?? '');
    setCategoryId(config.categoryId ?? '');
    setSubcategoryId(config.subcategoryId ?? '');
    setInicializado(true);
  }

  const salvar = useMutation({
    mutationFn: () =>
      salvarRoteamentoDeEmail({
        teamId: teamId || null,
        priority: (priority as EmailRoutingPriority) || null,
        categoryId: categoryId || null,
        subcategoryId: subcategoryId || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: chavesDeAdministracao.roteamentoDeEmail });
    },
  });

  const equipes = apenasAtivos(opcoes.data?.teams ?? []);
  const categorias = apenasAtivos(opcoes.data?.categories ?? []);
  const subcategorias = subcategoriasDaCategoria(opcoes.data, categoryId || null);
  const prioridades = listaDeImpactos(opcoes.data).filter((p) => p.active);

  if (consulta.isPending) {
    return (
      <div className="space-y-3" aria-busy>
        <div className="h-6 w-48 animate-pulse rounded bg-muted" />
        <div className="h-40 animate-pulse rounded-xl bg-muted" />
      </div>
    );
  }

  if (consulta.isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        {mensagemDeErro(consulta.error)}
      </p>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Roteamento de e-mail</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Valores padrão aplicados a todo chamado aberto automaticamente pela caixa de entrada.
          Campos em branco fazem o chamado entrar sem classificação — o operador preenche na triagem.
        </p>
      </div>

      {/* Endereço da caixa — somente leitura */}
      <Card>
        <CardBody>
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            Caixa de entrada
          </p>
          <p className="mt-1 font-mono text-sm font-medium">central@aptum.com.br</p>
          <p className="mt-1 text-xs text-muted-foreground">
            Mensagens enviadas a este endereço abrem novos chamados ou são adicionadas ao existente
            quando o remetente responde com o histórico.
          </p>
        </CardBody>
      </Card>

      {/* Formulário de roteamento */}
      <Card>
        <CardBody>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              salvar.mutate();
            }}
          >
            <p className="text-sm font-medium text-foreground">Padrões do chamado criado por e-mail</p>

            <div className="grid gap-4 sm:grid-cols-2">
              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Equipe</span>
                <select
                  value={teamId}
                  onChange={(e) => setTeamId(e.target.value)}
                  className={CAMPO}
                  disabled={opcoes.isPending}
                >
                  <option value="">— sem equipe padrão —</option>
                  {equipes.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Prioridade inicial</span>
                <select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className={CAMPO}
                  disabled={opcoes.isPending}
                >
                  <option value="">— calcular pelo impacto × urgência —</option>
                  {prioridades.map((p) => (
                    <option key={p.level} value={p.level}>
                      {p.label ?? ROTULO_PRIORIDADE[p.level as EmailRoutingPriority] ?? p.level}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Categoria</span>
                <select
                  value={categoryId}
                  onChange={(e) => {
                    setCategoryId(e.target.value);
                    setSubcategoryId('');
                  }}
                  className={CAMPO}
                  disabled={opcoes.isPending}
                >
                  <option value="">— sem categoria padrão —</option>
                  {categorias.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </label>

              <label className="block space-y-1.5">
                <span className="text-xs font-medium text-muted-foreground">Subcategoria</span>
                <select
                  value={subcategoryId}
                  onChange={(e) => setSubcategoryId(e.target.value)}
                  disabled={opcoes.isPending || subcategorias.length === 0}
                  className={CAMPO}
                >
                  <option value="">— sem subcategoria padrão —</option>
                  {subcategorias.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {salvar.isError ? (
              <p role="alert" className="text-sm text-destructive">
                {mensagemDeErro(salvar.error)}
              </p>
            ) : null}

            {salvar.isSuccess ? (
              <p className="text-sm text-success">Configuração salva.</p>
            ) : null}

            <div className="pt-1">
              <Button type="submit" loading={salvar.isPending}>
                Salvar configuração
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
